import axios from 'axios';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js'; // https://www.npmjs.com/package/mapbox-gl
import geoViewport from '@mapbox/geo-viewport'; // https://www.npmjs.com/package/@mapbox/geo-viewport
import Flickity from 'flickity'; // https://flickity.metafizzy.co/
import LazyLoadImage from '../classes/lazyloadimage'; // https://github.com/verlok/vanilla-lazyload
import {COORDS_FALLBACK} from '../helpers/coordsSession';
import * as maplibregl from 'maplibre-gl';
import {Marker} from "maplibre-gl";

export default class Mapbox {
    /**
     *
     * @param pointUrlRequest string url used to get all points for clusters
     * @param defaultCoords
     * @param zoom
     * @param onMapInit called after map init
     */
    constructor (pointUrlRequest, defaultCoords = COORDS_FALLBACK, zoom = 14, onMapInit = (instance) => {}) {
        this.pointUrlRequest = pointUrlRequest;
        this.defaultCoords = defaultCoords;
        this.zoom = zoom
        this.onMapInit = onMapInit;
        this.inResultMove = true;
        this.hoveredEventId = null;
        this.clickedEventId = null;
        this.clusterDefaultColor = '#FF3E60';
        this.clusterHoverColor = '#0098FF';
        this.hoveredCard = [];
        this.hoveredCardScroll = null;
        this.hoveredCardEventId = null;
    }

    init() {
        if (document.getElementById('map')) {
            this.setupColors();
            this.initElements();
            this.initEvents();
            this.initMap();
        }
    }

    setupColors() {
        const style = window.getComputedStyle(document.body)

        const mainColor = style.getPropertyValue('--main-color');
        const lightMainColor = style.getPropertyValue('--light-main-color');

        this.clusterDefaultColor = mainColor || this.clusterDefaultColor;
        this.clusterHoverColor = lightMainColor || this.clusterHoverColor;
    }

    initElements() {
        this.data = null;
        this.map_container = document.querySelector('.map-container');
        this.lazyLoading = new LazyLoadImage();
        this.resizeCallback = () => {};
    }

    initEvents() {
        window.addEventListener('resize', () => {
            this.resizeCallback();
        });
    }

    destroySlider() {
        if (this.slider) {
            this.slider.destroy();
            this.slider = null;
        }

        let popupEvents = document.getElementsByClassName('popup-events')[0];

        if (popupEvents) {
            popupEvents.innerHTML = '';
        }
        if (this.clickedEventId) {
            this.map.removeFeatureState({
                source: 'events',
                id: this.clickedEventId
            });
            this.clickedEventId = null;
        }
    }

    /**
     * Init Map, trigger click Events, update buttons, drag event
     */
    initMap() {
        let coords = this.defaultCoords;
        let zoom_in = document.getElementById('zoom-in');
        let zoom_out = document.getElementById('zoom-out');

        zoom_in.addEventListener('click', () => {
            this.map.zoomIn();
        });

        zoom_out.addEventListener('click', () => {
            this.map.zoomOut();
        });

        // init map
        this.map = new mapboxgl.Map({
            container: 'map',
            style: 'https://map.infomaniak.com/styles/basic-preview/style.json',
            center: [coords.lon, coords.lat],
            zoom: this.zoom,
            maxZoom: 22,
            minZoom: 1
        });

        this.map.dragRotate.disable();

        this.map.addControl(new maplibregl.AttributionControl({
            customAttribution: '<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
            compact: false
        }));

        this.map.on('load', () => {
            // Cluster
            axios.get(this.pointUrlRequest).then(response => {
                this.data = response.data;
                if (this.map.getSource('events')) {
                    this.map.getSource('events').setData({
                        type: 'geojson',
                        data: {
                            'type': 'FeatureCollection',
                            'features': this.data.filter(data => data.data?.length > 0).map(data => ({
                                'type': 'Feature',
                                'properties': {
                                    'html': data.html,
                                    'unique_id': data.id,
                                },
                                'geometry': {
                                    'type': 'Point',
                                    'coordinates': [data.data[0].lon, data.data[0].lat]
                                },
                                id: data.id
                            })),
                            generateId: true,
                            promoteId: 'id'
                        },
                        cluster: true,
                        clusterMaxZoom: 14,
                        clusterRadius: 20
                    });
                } else {
                    this.map.addSource('events', {
                        type: 'geojson',
                        data: {
                            'type': 'FeatureCollection',
                            'features': this.data.filter(data => data.data?.length > 0).map(data => ({
                                'type': 'Feature',
                                'properties': {
                                    'html': data.html,
                                    'unique_id': data.id
                                },
                                'geometry': {
                                    'type': 'Point',
                                    'coordinates': [data.data[0].lon, data.data[0].lat]
                                },
                                id: data.id
                            })),
                            generateId: true,
                            promoteId: 'id'
                        },
                        cluster: true,
                        clusterMaxZoom: 14,
                        clusterRadius: 20
                    });
                }

                this.map.addLayer({
                    id: 'clusters',
                    type: 'circle',
                    source: 'events',
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': [
                            'case',
                            ['boolean', ['feature-state', 'active'], false],
                            this.clusterHoverColor,
                            ['boolean', ['feature-state', 'hover'], false],
                            this.clusterHoverColor,
                            this.clusterDefaultColor
                        ],
                        'circle-radius': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            [
                                'step',
                                ['get', 'point_count'],
                                20,
                                5,
                                25,
                                10,
                                35,
                            ],
                            [
                                'step',
                                ['get', 'point_count'],
                                15,
                                5,
                                20,
                                10,
                                30
                            ]
                        ]
                    },
                });

                this.map.addLayer({
                    id: 'cluster-count',
                    type: 'symbol',
                    source: 'events',
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': '{point_count_abbreviated}',
                        'text-font': ['Noto Sans Regular'], // only this font seems to be available on map.infomaniak.com
                        'text-size':
                            [
                                'step',
                                ['get', 'point_count'],
                                15,
                                5,
                                20,
                                10,
                                30,
                                99,
                                0
                            ]
                    },
                    paint: {
                        'text-color': '#ffffff'
                    }
                });

                this.map.addLayer({
                    id: 'unclustered-point',
                    type: 'circle',
                    source: 'events',
                    filter: ['!', ['has', 'point_count']],
                    paint: {
                        'circle-color': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            this.clusterHoverColor,
                            this.clusterDefaultColor
                        ],
                        'circle-radius': [
                            'case',
                            ['boolean', ['feature-state', 'hover'], false],
                            13,
                            8
                        ],
                    }
                });

                // Close popups when you click on the map
                this.map.on('click', () => {
                    this.destroySlider();
                });

                this.map.on('moveend', () => {
                    if (this.map_container.classList.contains('open')) {
                        let event = new CustomEvent('map_changes', {
                            bubbles: true, // Allow the event to bubble up the DOM tree
                        });
                        // Dispatch the custom event on the document or the specific element
                        document.dispatchEvent(event);
                    }
                });

                // when you click on a cluster => zoom
                const _zoomOnCluster = this._zoomOnCluster.bind(this);
                const _hoverOnCluster = this._hoverOnCluster.bind(this);
                const _setCursorStyle = this._setCursorStyle.bind(this);
                const clickOnUnclusteredPoint = this.clickOnUnclusteredPoint.bind(this);

                this.map.on('click', 'clusters', _zoomOnCluster);

                this.map.on('mouseenter', 'clusters', _hoverOnCluster);

                this.map.on('mouseenter', 'clusters', _setCursorStyle);

                this.map.on('mouseleave', 'clusters', _setCursorStyle);

                this.map.on('mouseleave', 'clusters', _hoverOnCluster);

                this.map.on('click', 'unclustered-point', clickOnUnclusteredPoint);

                this.map.on('mouseenter', 'unclustered-point', _hoverOnCluster);

                this.map.on('mouseenter', 'unclustered-point', _setCursorStyle);

                this.map.on('mouseleave', 'unclustered-point', _setCursorStyle);

                this.map.on('mouseleave', 'unclustered-point', _hoverOnCluster);

                this.map.once('remove', () => {
                    this.map.off('click', 'clusters', _zoomOnCluster);
                    this.map.off('mouseenter', 'clusters', _setCursorStyle);
                    this.map.off('mouseleave', 'clusters', _setCursorStyle);
                    this.map.off('mouseenter', 'unclustered-point', _setCursorStyle);
                    this.map.off('mouseleave', 'unclustered-point', _setCursorStyle);
                    this.map.off('click', 'unclustered-point', displayPopup);
                });

                this.map.once('idle', () => {
                    this.map.resize();
                    this.onMapInit(this);
                    document.querySelector('.mapboxgl-canvas').classList.add('active');
                    document.querySelector('#map .loader__init').remove();
                });

                let markersOnScreen = {};
                const markers = {};
                this.map.on('render', () => {
                    if (!this.map.isSourceLoaded('events')) {
                        return;
                    }
                    let features = this.map.queryRenderedFeatures({layers: ['clusters'], validate: false});
                    const newMarkers = {};
                    features.forEach((feature) => {
                        if (feature.properties.point_count < 100) {
                            return;
                        }
                        let marker = markers[feature.properties.cluster_id];
                        if (!marker) {
                            const el = document.createElement('div');
                            el.innerHTML = `<div><svg width="50" height="50" viewBox="0 0 50 50" text-anchor="middle" style="font-size: 20px; display: block;"><text fill="#ffffff" dominant-baseline="central" transform="translate(25, 25)">${Math.round(feature.properties.point_count / 10) * 10}+</text></svg></div>`;
                            marker = markers[feature.properties.cluster_id] = new Marker({element: el.firstChild}).setLngLat(feature.geometry.coordinates);
                        }
                        newMarkers[feature.properties.cluster_id] = marker;
                        if (!markersOnScreen[feature.properties.cluster_id]) {
                            marker.addTo(this.map);
                        }
                    });
                    for (const id in markersOnScreen) {
                        if (!newMarkers[id]) {
                            markersOnScreen[id].remove();
                        }
                    }
                    markersOnScreen = newMarkers;
                })
            });
        });
    }

    /**
     *
     * @param e
     * @private
     */
    _zoomOnCluster(e) {
        try {
            let features = this.map.queryRenderedFeatures(e.point, {
                layers: ['clusters']
            });
            const clusterId = features[0].properties.cluster_id;
            this.map.getSource('events').getClusterExpansionZoom(
                clusterId,
                (err, zoom) => {
                    if (err) return;
                    this.map.easeTo({
                        center: features[0].geometry.coordinates,
                        zoom: zoom
                    });
                }
            );
        } catch (e) {
        }
    }

    _hoverOnCluster(e) {
        if (e.type === 'mouseenter') {
            if (this.hoveredEventId && this.hoveredEventId !== this.hoveredCardEventId) {
                this.map.removeFeatureState({
                    source: 'events',
                    id: this.hoveredEventId
                });
            }

            this.hoveredEventId = e.features[0].id;

            this.map.setFeatureState(
                {
                    source: 'events',
                    id: this.hoveredEventId
                },
                {
                  hover: true
                }
            );
        } else {
            if (this.hoveredEventId && this.hoveredEventId !== this.hoveredCardEventId && this.hoveredEventId !== this.clickedEventId) {
                this.map.removeFeatureState({
                    source: 'events',
                    id: this.hoveredEventId
                });
            }
            this.hoveredEventId = null;
        }
    }

    /**
     *
     * @param e
     * @private
     */
    _setCursorStyle(e) {
        this.map.getCanvas().style.cursor = e.type === 'mouseenter' ? 'pointer' : '';
    }

    clickOnUnclusteredPoint(e) {
    const features = e.features;
    let coordinates = features[0].geometry.coordinates.slice();
    if (this.clickedEventId) {
        this.map.removeFeatureState({
            source: 'events',
            id: this.clickedEventId
        });
    }

    this.clickedEventId = features[0].id;

    this.map.setFeatureState(
        { source: 'events', id: this.clickedEventId },
        { active: true }
    );


    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    let html = document.createElement('div');
    features.forEach(element => {
      html.append(element.properties.html);
    });

    let popupEvents = document.getElementsByClassName('popup-events')[0];
    popupEvents.innerHTML = html.innerText;
    popupEvents.classList.add('active');
    popupEvents.style.display = 'flex';
    popupEvents.style.justifyContent = 'center';
    popupEvents.querySelectorAll('.event-card').forEach(e => {
      e.classList.add('map-card');
    })

    this.lazyLoading.lazyLoading();

    document.querySelectorAll('.mapboxgl-popup-content .event-card').forEach((element) => {
      element.classList.add('multiple-card');
    });

    this.slider = null;

    this.resizeCallback = () => {
      let cardNumber = 3;
      if (window.outerWidth < 1200) {
        cardNumber = 1;
      } else if (window.outerWidth > 1200) {
        cardNumber = 2;
      }

      if (features.length > cardNumber) {
        if (this.slider) {
          return;
        }
        popupEvents.style.display = 'inherit';
        popupEvents.style.justifyContent = 'inherit';

        this.slider = new Flickity('.popup-events', {
          contain: true,
          cellAlign: 'center',
          pageDots: false,
          wrapAround: true,
          prevNextButtons: true,
          friction: 0.8,
          selectedAttraction: 0.2
        });

        let icon_right = document.createElement('i');
        let icon_left = document.createElement('i');

        icon_right.classList.add('icon', 'main-color', 'icon-chevron-right');
        icon_left.classList.add('icon', 'main-color', 'icon-chevron-left');

        document.querySelector('.flickity-button.next').replaceChild(icon_right, document.querySelector('.flickity-button.next').firstElementChild);
        document.querySelector('.flickity-button.previous').replaceChild(icon_left, document.querySelector('.flickity-button.previous').firstElementChild);
      } else if (this.slider) {
        this.slider.destroy();
        this.slider = null;
        popupEvents.style.display = 'flex';
        popupEvents.style.justifyContent = 'center';

      }
    }

    this.resizeCallback();
  }

    /**
     *
     * @returns {*}
     */
    getCurrentZoneSearch() {
        return this.map.getCenter().wrap();
    }

    /**
     *
     * @returns {*}
     */
    getCurrentMapSize() {
        return geoViewport.bounds([this.map.getCenter().lng, this.map.getCenter().lat], this.map.getZoom(), [this.map.transform.width, this.map.transform.height], this.map.transform.tileSize);
    }

    /**
     * Method to update data
     */
    updateMapData(data, reloadData = false, recenter = true) {
        if (reloadData || !this.data) {
            this.data = data;
        } else {
            // Merge the arrays, avoiding duplicates based on 'id'
            const existingIds = new Set(this.data.map(item => item.id));
            this.data = [...this.data, ...data.filter(item => !existingIds.has(item.id))];
        }

        if (this.map.getSource('events')) {
            this.map.getSource('events').setData( {
                'type': 'FeatureCollection',
                'features': this.data.filter(data => data.data?.length > 0).map(data => ({
                    'type': 'Feature',
                    'properties': {
                        'html': data.html,
                        'unique_id': data.id,
                    },
                    'geometry': {
                        'type': 'Point',
                        'coordinates': [data.data[0].lon, data.data[0].lat]
                    },
                    id: data.id
                })),
                generateId: true,
                promoteId: 'id'
            });
        } else {
            // this.map.addSource('events', {
            //     type: 'geojson',
            //     data: {
            //         'type': 'FeatureCollection',
            //         'features': this.data.filter(data => data.data?.length > 0).map(data => ({
            //             'type': 'Feature',
            //             'geometry': {
            //                 'type': 'Point',
            //                 'coordinates': [data.data[0].lon, data.data[0].lat]
            //             }
            //         }))
            //     },
            //     cluster: true,
            //     clusterMaxZoom: 14,
            //     clusterRadius: 20
            // });
        }
        if (recenter) {
            this.setMapCenter();
        }
    }

    setMapCenter() {
        const first = this.data.find(data => data.data?.length > 0);

        if (first) {
            this.map.setCenter([first.data[0].lon, first.data[0].lat])
            if(this.data?.length > 1){
                this.inResultMove = true;
                const allCoord = this.data.filter((data) => data.data?.length > 0).map((data) => (
                    {lng: data.data[0].lon, lat: data.data[0].lat}
                ))

                this.map.fitBounds([
                    [allCoord.sort((a,b) => a.lng - b.lng)[0].lng, allCoord.sort((a,b) => a.lat - b.lat)[0].lat],
                    [allCoord.sort((a,b) => a.lng - b.lng)[allCoord.length-1].lng, allCoord.sort((a,b) => a.lat - b.lat)[allCoord.length-1].lat],
                ], {padding: 100})
                this.map.once('idle', () => {
                    this.inResultMove = false
                });

                // this.map.setCenter(mapCameraOption.center)
                // this.map.setZoom(test._easeOptions.zoom.zoom-3)
            }
        }
    }
}
